/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { Kontakt } from '../models/Kontakt';
import type { KontaktaufnahmeImportDaten } from '../models/KontaktaufnahmeImportDaten';
import type { KontaktaufnahmeImportDatenGandayo } from '../models/KontaktaufnahmeImportDatenGandayo';
import type { KontakteImportReport } from '../models/KontakteImportReport';
import type { KontaktePromptInsert } from '../models/KontaktePromptInsert';
import type { KontaktePromptInsertData } from '../models/KontaktePromptInsertData';
import type { KontaktWithTagData } from '../models/KontaktWithTagData';
import type { ReserveCallDaten } from '../models/ReserveCallDaten';
import type { ReserveCallResponse } from '../models/ReserveCallResponse';

import type { CancelablePromise } from '../core/CancelablePromise';
import type { BaseHttpRequest } from '../core/BaseHttpRequest';

export class KontakteService {

  constructor(public readonly httpRequest: BaseHttpRequest) {}

  /**
   * Kontakte importieren
   * Importiert Kontakte von Meta
   * @returns Kontakt Die erstellten Kontakte
   * @throws ApiError
   */
  public importKontakt({
    requestBody,
  }: {
    /**
     * Daten zu den Kontakten, welche erstellt/importiert werden sollen
     */
    requestBody: Array<KontaktaufnahmeImportDaten>,
  }): CancelablePromise<Array<Kontakt>> {
    return this.httpRequest.request({
      method: 'POST',
      url: '/api/v1/kontakte/import',
      body: requestBody,
      mediaType: 'application/json',
      errors: {
        401: `Unauthorized for this request (importKontakt)`,
        403: `Forbidden for this request (importKontakt)`,
      },
    });
  }

  /**
   * Kontakte von Gandayo importieren
   * Importiert Kontakte von Gandayo
   * @returns Kontakt Die erstellten Kontakte
   * @throws ApiError
   */
  public importKontaktGandayo({
    requestBody,
  }: {
    /**
     * Daten zu den Kontakten, welche erstellt/importiert werden sollen
     */
    requestBody: Array<KontaktaufnahmeImportDatenGandayo>,
  }): CancelablePromise<Array<Kontakt>> {
    return this.httpRequest.request({
      method: 'POST',
      url: '/api/v1/kontakte/import/gandayo',
      body: requestBody,
      mediaType: 'application/json',
      errors: {
        401: `Unauthorized for this request (importKontaktGandayo)`,
        403: `Forbidden for this request (importKontaktGandayo)`,
      },
    });
  }

  /**
   * Eingehende Lead-Daten verarbeiten
   * Nimmt externe Lead-Daten als Liste entgegen und startet deren Verarbeitung.
   * @returns Kontakt Kontakt
   * @throws ApiError
   */
  public importKontaktWithTagData({
    requestBody,
  }: {
    requestBody: Array<KontaktWithTagData>,
  }): CancelablePromise<Array<Kontakt>> {
    return this.httpRequest.request({
      method: 'POST',
      url: '/api/v1/kontakte/import-with-tag-data',
      body: requestBody,
      mediaType: 'application/json',
      errors: {
        400: `Ungültige Anfrage`,
        401: `Unauthorized for this request (importKontaktWithTagData)`,
        403: `Forbidden for this request (importKontaktWithTagData)`,
      },
    });
  }

  /**
   * Call für eine Telefonnummer reservieren
   * Reserviert einen Call für die angegebene Telefonnummer innerhalb der angegebenen Kontaktaufgabe und liefert die Identifikatoren sowie die Callback-URL zurück.
   * @returns ReserveCallResponse Der reservierte Call
   * @throws ApiError
   */
  public reserveCall({
    requestBody,
  }: {
    /**
     * Daten zur Reservierung des Calls
     */
    requestBody: ReserveCallDaten,
  }): CancelablePromise<ReserveCallResponse> {
    return this.httpRequest.request({
      method: 'POST',
      url: '/api/v1/kontakte/reserve-call',
      body: requestBody,
      mediaType: 'application/json',
      errors: {
        400: `Ungültige Anfrage`,
        401: `Unauthorized for this request (reserveCall)`,
        403: `Forbidden for this request (reserveCall)`,
      },
    });
  }

  /**
   * Kontakte per CSV importieren
   * Importiert Kontakte aus einer CSV-Datei. Ungültige Zeilen werden übersprungen. Gibt einen Import-Report zurück. Die Kontakte werden alle gescheduled.
   * @returns KontakteImportReport Import-Report
   * @throws ApiError
   */
  public importKontakteCsv({
    aufgabeId,
    formData,
  }: {
    /**
     * ID der Aufgabe, der die importierten Kontakte zugeordnet werden
     */
    aufgabeId: string,
    formData: {
      file: Blob;
    },
  }): CancelablePromise<KontakteImportReport> {
    return this.httpRequest.request({
      method: 'POST',
      url: '/api/v1/aufgaben/{aufgabeId}/kontakte/import-csv',
      path: {
        'aufgabeId': aufgabeId,
      },
      formData: formData,
      mediaType: 'multipart/form-data',
      errors: {
        400: `Ungültiges Dateiformat oder CSV-Struktur`,
        401: `Unauthorized for this request (importKontakteCsv)`,
        403: `Forbidden for this request (importKontakteCsv)`,
        413: `Datei zu groß`,
      },
    });
  }

  /**
   * Template-CSV für Kontakte-Import herunterladen
   * Liefert die Template-CSV als Download. Der Response enthält einen Content-Disposition: attachment Header.
   * @returns binary Template CSV
   * @throws ApiError
   */
  public getKontakteImportTemplate(): CancelablePromise<Blob> {
    return this.httpRequest.request({
      method: 'GET',
      url: '/api/v1/kontakte/import-template',
      errors: {
        401: `Unauthorized for this request (getKontakteImportTemplate)`,
        403: `Forbidden for this request (getKontakteImportTemplate)`,
      },
    });
  }

  /**
   * Ein Kontakt bekommen
   * Bekomme ein Kontakt
   * @returns Kontakt Der Kontakt
   * @throws ApiError
   */
  public getKontakt({
    kontaktId,
  }: {
    /**
     * Eindeutige ID des Kontaktes für (getKontakt)
     */
    kontaktId: string,
  }): CancelablePromise<Kontakt> {
    return this.httpRequest.request({
      method: 'GET',
      url: '/api/v1/kontakte/{kontaktId}',
      path: {
        'kontaktId': kontaktId,
      },
      errors: {
        401: `Unauthorized for this request (getKontakt)`,
        403: `Forbidden for this request (getKontakt)`,
        404: `Not Found for this request (getKontakt)`,
      },
    });
  }

  /**
   * Prompt Inserts für einen Kontakt abrufen
   * Gibt alle Prompt Inserts für einen Kontakt zurück
   * @returns KontaktePromptInsert Liste der Prompt Inserts für den Kontakt
   * @throws ApiError
   */
  public getKontaktePromptInsertsByKontaktId({
    kontaktId,
    sprache,
    tag,
  }: {
    /**
     * Eindeutige ID des Kontaktes
     */
    kontaktId: string,
    /**
     * Filter nach Sprache
     */
    sprache?: string,
    /**
     * Filter nach Tag
     */
    tag?: string,
  }): CancelablePromise<Array<KontaktePromptInsert>> {
    return this.httpRequest.request({
      method: 'GET',
      url: '/api/v1/kontakte/{kontaktId}/kontakte-prompt-inserts',
      path: {
        'kontaktId': kontaktId,
      },
      query: {
        'sprache': sprache,
        'tag': tag,
      },
      errors: {
        401: `Unauthorized for this request`,
        403: `Forbidden for this request`,
        404: `Not Found for this request`,
      },
    });
  }

  /**
   * Prompt Insert(s) erstellen
   * Erstellt einen oder mehrere neue Prompt Inserts für Kontakte
   * @returns KontaktePromptInsert Die erstellten Prompt Inserts
   * @throws ApiError
   */
  public createKontaktePromptInsert({
    requestBody,
  }: {
    requestBody: Array<KontaktePromptInsertData>,
  }): CancelablePromise<Array<KontaktePromptInsert>> {
    return this.httpRequest.request({
      method: 'POST',
      url: '/api/v1/kontakte/kontakte-prompt-inserts',
      body: requestBody,
      mediaType: 'application/json',
      errors: {
        400: `Bad Request for this request`,
        401: `Unauthorized for this request`,
        403: `Forbidden for this request`,
      },
    });
  }

  /**
   * Prompt Insert abrufen
   * Gibt einen spezifischen Prompt Insert zurück
   * @returns KontaktePromptInsert Der Prompt Insert
   * @throws ApiError
   */
  public getKontaktePromptInsert({
    promptInsertId,
  }: {
    /**
     * Eindeutige ID des Prompt Inserts
     */
    promptInsertId: string,
  }): CancelablePromise<KontaktePromptInsert> {
    return this.httpRequest.request({
      method: 'GET',
      url: '/api/v1/kontakte/kontakte-prompt-inserts/{promptInsertId}',
      path: {
        'promptInsertId': promptInsertId,
      },
      errors: {
        401: `Unauthorized for this request`,
        403: `Forbidden for this request`,
        404: `Not Found for this request`,
      },
    });
  }

  /**
   * Prompt Insert löschen
   * Löscht einen Prompt Insert
   * @returns void
   * @throws ApiError
   */
  public deleteKontaktePromptInsert({
    promptInsertId,
  }: {
    /**
     * Eindeutige ID des Prompt Inserts
     */
    promptInsertId: string,
  }): CancelablePromise<void> {
    return this.httpRequest.request({
      method: 'DELETE',
      url: '/api/v1/kontakte/kontakte-prompt-inserts/{promptInsertId}',
      path: {
        'promptInsertId': promptInsertId,
      },
      errors: {
        401: `Unauthorized for this request`,
        403: `Forbidden for this request`,
        404: `Not Found for this request`,
      },
    });
  }

}
